import { useContext, useRoute } from "@nuxtjs/composition-api";
import TRACK_FUNNEL_EVENT from "~/pages/request-for-tender/trackFunnelEvent.graphql";

const SESSION_STORAGE_KEY = "rftFunnelSessionId";

// UUID v4 — small, dependency-free.
function uuidv4() {
  const rnd = (n) =>
    [...Array(n)]
      .map(() => Math.floor(Math.random() * 16).toString(16))
      .join("");
  const a = rnd(8);
  const b = rnd(4);
  const c = "4" + rnd(3);
  // y in [8,9,a,b]
  const yChar = "89ab"[Math.floor(Math.random() * 4)];
  const d = yChar + rnd(3);
  const e = rnd(12);
  return `${a}-${b}-${c}-${d}-${e}`;
}

function getOrCreateSessionId() {
  if (!process.client) return null;
  try {
    let id = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (!id) {
      id = uuidv4();
      sessionStorage.setItem(SESSION_STORAGE_KEY, id);
    }
    return id;
  } catch (e) {
    // sessionStorage can throw in private mode — fall back to a per-call id
    // so telemetry still flows, just without cross-step grouping.
    return uuidv4();
  }
}

// PostHog rotates $session_id after 30 min inactivity, so this must be
// called per event, never cached at wizard start. $posthog is null outside
// production and a method-less dummy on init failure.
function getPosthogSessionId(posthog) {
  try {
    const id =
      typeof posthog?.get_session_id === "function"
        ? posthog.get_session_id()
        : null;
    return id || null;
  } catch (e) {
    return null;
  }
}

/**
 * Funnel-event emitter for the request-tender wizard.
 *
 * Fire-and-forget. We never surface errors to the user (showErrorToast/Page
 * false) — telemetry MUST NOT break the wizard. Until the backend mutation
 * resolver exists, every call silently 4xxs.
 *
 * See `request-tender-page-funnel-spec.md` for the data model and how the
 * conductor dashboard consumes these events.
 */
export default function useRequestTenderPageFunnel() {
  // Brand is intentionally NOT pulled from the client — the spec relies on
  // the server reading it from the request context so events can't be
  // forged into another brand's data.
  const { $mutate, $posthog } = useContext();
  const route = useRoute();

  const send = (eventType, extras = {}) => {
    if (!process.client) return;
    const sessionId = getOrCreateSessionId();
    if (!sessionId) return;
    const query = route.value?.query || {};
    // Brand is used server-side via the request context per spec section 2,
    // but pass nothing brand-related from the client — server trust only.
    const variables = {
      sessionId,
      eventType,
      moduleVersionId: extras.moduleVersionId || null,
      professionId: extras.professionId || query.professionId || null,
      stepSlug: extras.stepSlug || null,
      stepPosition: extras.stepPosition ?? null,
      totalSteps: extras.totalSteps ?? null,
      personId: query.personId || null,
      cityId: extras.cityId || query.cityId || null,
      posthogSessionId: getPosthogSessionId($posthog),
    };
    // Don't await — telemetry never blocks navigation. Swallow rejections so
    // an offline / 4xx / 5xx never surfaces. skipErrorPageOnAuthError is what
    // keeps an UNAUTHENTICATED/FORBIDDEN response from routing the user to the
    // error page — showErrorPage: false alone does not cover the auth codes.
    $mutate(TRACK_FUNNEL_EVENT, variables, {
      showErrorPage: false,
      showErrorToast: false,
      skipErrorPageOnAuthError: true,
    }).catch(() => {});
  };

  return {
    trackStepView({
      moduleVersionId,
      professionId,
      stepSlug,
      stepPosition,
      totalSteps,
    } = {}) {
      send("step_view", {
        moduleVersionId,
        professionId,
        stepSlug,
        stepPosition,
        totalSteps,
      });
    },
    // A step the wizard auto-skips because its value came from a URL prefill
    // (?professionId, ?cityId): the user never sees it, but we still record it
    // — as its own event type, not a `step_view` — so it counts toward the
    // funnel (keeping it monotonic) while staying distinguishable from a step
    // the user actually viewed. The conductor read side unions both event
    // types for per-step counts and breaks this one out separately.
    trackStepSkipAutoFilled({
      moduleVersionId,
      professionId,
      stepSlug,
      stepPosition,
      totalSteps,
    } = {}) {
      send("step_skip_auto_filled", {
        moduleVersionId,
        professionId,
        stepSlug,
        stepPosition,
        totalSteps,
      });
    },
    trackSubmit({ moduleVersionId, professionId } = {}) {
      send("submit", { moduleVersionId, professionId });
    },
  };
}
